import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["lesson", "onFinish"];
import PortalIdParser from 'PortalIdParser';
import { useMemo } from 'react';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { usePracticalExerciseStore } from 'academy-common-lib/features/practicalExercises/practicalExerciseStore';
import { unwrapChirpError } from 'academy-common-lib/utils/DataFetchingClient';
import { reportErrorToLogFetch } from 'academy-common-lib/utils/errorUtils';
import CompletedPracticalExerciseCondensed from './CompletedPracticalExerciseCondensed';
import FailedPracticalExerciseCondensed from './FailedPracticalExerciseCondensed';
import InProgressPracticalExercise from './InProgressPracticalExercise';
import InReviewPracticalExerciseCondensed from './InReviewPracticalExerciseCondensed';
import NotStartedPracticalExercise from './NotStartedPracticalExercise';
import PracticalExerciseError from './PracticalExerciseError';
import PracticalExerciseLoading from './PracticalExerciseLoading';
import PracticalExerciseRequiredAccess from './PracticalExerciseRequiredAccess';
import PracticalExerciseRequiredProduct from './PracticalExerciseRequiredProduct';
import PracticalExerciseWithSubmission from './PracticalExerciseWithSubmission';
import UsageTracker from '../../../utils/UsageTracker';
import { useIsSimulationExercise, usePracticalExerciseRequirements, useSetManuallyGradedPracticalExerciseWithAssetSubmitted, useSetPracticalExerciseStarted, useDeletePracticalExerciseProgress } from '../practicalExerciseQueries';
import { navigateToExercise } from '../practicalExerciseHooks';
import { usePlaylistIdFromSearch } from '../../playlistContent/hooks.migrated';
import { isPracticalExerciseAsset } from '../../tracks/tracksUtils';
import { ContactUsLink } from '../../../components/ContactUsLink';
import { ContentProgressTypes, PracticalExerciseTypes } from '../../../constants/ContentConstants';
import { Metrics } from '../../../Metrics';
import { jsx as _jsx } from "react/jsx-runtime";
export const WEBPACK_3_FORCE_MODULE_IMPORT = 1;
export const PracticalExercise = ({
  content,
  lesson,
  track,
  contentProgress,
  onFinish
}) => {
  var _chirpExercise$exerci, _chirpExercise$tracke;
  const contentId = content.id;
  const lessonId = lesson.id;
  const trackId = track === null || track === void 0 ? void 0 : track.id;
  const trackName = track === null || track === void 0 ? void 0 : track.name;
  const lessonName = lesson.name;
  const contentStatus = (contentProgress === null || contentProgress === void 0 ? void 0 : contentProgress.progressType) || 'NOT_STARTED';
  const practicalExercise = useMemo(() => {
    if (!lesson || !lesson.content) {
      return null;
    }
    const foundExercise = lesson.content.find(c => c.id === content.id);
    return foundExercise;
  }, [content.id, lesson]);
  const {
    practicalExerciseRequirements,
    practicalExerciseRequirementsLoading,
    practicalExerciseRequirementsError
  } = usePracticalExerciseRequirements({
    contentId
  });
  const chirpExercise = practicalExercise && isPracticalExerciseAsset(practicalExercise) ? practicalExercise : null;
  const isManuallyGraded = chirpExercise === null || chirpExercise === void 0 ? void 0 : chirpExercise.manuallyApproved;
  const exerciseLocation = (_chirpExercise$exerci = chirpExercise === null || chirpExercise === void 0 ? void 0 : chirpExercise.exerciseLocation) !== null && _chirpExercise$exerci !== void 0 ? _chirpExercise$exerci : '';
  const trackerNotRequired = (_chirpExercise$tracke = chirpExercise === null || chirpExercise === void 0 ? void 0 : chirpExercise.trackerNotRequired) !== null && _chirpExercise$tracke !== void 0 ? _chirpExercise$tracke : false;
  const practicalExerciseType = chirpExercise === null || chirpExercise === void 0 ? void 0 : chirpExercise.type;
  const exerciseTypeLabel = practicalExerciseType ? readUnhandledEnumCase(practicalExerciseType).toLowerCase().replace(/_/g, '-') : 'none';
  const [startPracticalExercise] = useSetPracticalExerciseStarted();
  const [submitPracticalExerciseWithAsset] = useSetManuallyGradedPracticalExerciseWithAssetSubmitted();
  const [deletePracticalExerciseProgress] = useDeletePracticalExerciseProgress();
  const {
    isSimulationExercise
  } = useIsSimulationExercise(contentId, trackId);
  const {
    startExerciseContext
  } = usePracticalExerciseStore();
  const playlistId = usePlaylistIdFromSearch();
  const trackExercise = action => {
    const graded = practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.graded;
    const name = practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.name;
    const requiredProduct = practicalExerciseRequirements && practicalExerciseRequirements.requiredProducts && practicalExerciseRequirements.requiredProducts[0];
    const trackingMethod = trackerNotRequired ? UsageTracker.track : UsageTracker.trackExternalLink;
    trackingMethod.call(UsageTracker, 'contentInteraction', {
      action,
      contentId,
      contentLanguage: practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.language,
      contentType: 'practical-exercise',
      trackId,
      trackName,
      lessonId,
      lessonName,
      contentName: name,
      productName: requiredProduct || '',
      isUntrackedExercise: trackerNotRequired,
      graded: !!graded,
      manuallyApproved: isManuallyGraded
    });
  };
  const handleClickThroughToProductPage = () => trackExercise('click-to-product-page');
  const setExerciseContext = simulationPortalId => {
    if (!practicalExercise) {
      return;
    }
    startExerciseContext({
      name: practicalExercise.name,
      description: practicalExercise.description,
      id: Number(contentId),
      exerciseLocation,
      lessonId,
      trackId,
      portalId: simulationPortalId || PortalIdParser.getPortalIdFromPath(),
      sourcePortalId: PortalIdParser.getPortalIdFromPath(),
      isSimulation: isSimulationExercise,
      isGraded: practicalExercise.graded,
      isManuallyGraded,
      playlistId
    });
  };
  const handleStartExercise = simulationPortalId => {
    trackExercise('click-start-exercise');
    return startPracticalExercise(lessonId, contentId, trackId).then(() => {
      setExerciseContext(simulationPortalId);
      Metrics.counter('exercise-content-mutation', {
        action: 'practical-exercise-started',
        exerciseType: exerciseTypeLabel
      }).increment();
      navigateToExercise(exerciseLocation, simulationPortalId);
    }).catch(error => {
      Metrics.counter('exercise-mutation-call-error', {
        action: 'practical-exercise-started-error',
        exerciseType: exerciseTypeLabel
      }).increment();
      reportErrorToLogFetch({
        loggingTitle: 'Practical Exercise Started Error',
        action: 'practical-exercise-started-error',
        error,
        additionalData: {
          contentId,
          lessonId,
          trackId
        }
      });
      FloatingAlertStore.addAlert({
        type: 'danger',
        message: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "learningCenter.practicalExercise.startError",
          options: {
            contactUsLink: /*#__PURE__*/_jsx(ContactUsLink, {})
          }
        })
      });
    });
  };
  const handleTryExerciseAgain = simulationPortalId => {
    setExerciseContext(simulationPortalId);
    trackExercise('click-revisit-exercise');
    navigateToExercise(exerciseLocation, simulationPortalId);
    return Promise.resolve();
  };
  const handleResumeExercise = simulationPortalId => {
    setExerciseContext(simulationPortalId);
    trackExercise('click-resume-exercise');
    navigateToExercise(exerciseLocation, simulationPortalId);
  };
  const handleSubmitManualExercise = (assetType, reference) => {
    trackExercise('click-review-exercise');
    return submitPracticalExerciseWithAsset({
      contentId,
      assetType,
      reference,
      lessonId,
      trackId
    }).then(() => {
      Metrics.counter('exercise-content-mutation', {
        action: 'practical-exercise-manual-submitted',
        exerciseType: exerciseTypeLabel
      }).increment();
    }).catch(error => {
      Metrics.counter('exercise-mutation-call-error', {
        action: 'practical-exercise-manual-submit-error',
        exerciseType: exerciseTypeLabel
      }).increment();
      reportErrorToLogFetch({
        loggingTitle: 'Practical Exercise Manual Submitted Error',
        action: 'practical-exercise-manual-submit-error',
        error,
        additionalData: {
          contentId,
          lessonId,
          trackId
        }
      });
      FloatingAlertStore.addAlert({
        type: 'danger',
        message: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "learningCenter.practicalExercise.submitError",
          options: {
            contactUsLink: /*#__PURE__*/_jsx(ContactUsLink, {})
          }
        })
      });
    });
  };
  const handleClickResubmit = () => {
    trackExercise('retry-practical-exercise');
    deletePracticalExerciseProgress({
      contentId,
      lessonId,
      trackId
    }).then(() => {
      Metrics.counter('exercise-content-mutation', {
        action: 'practical-exercise-resubmit-progress-deleted',
        exerciseType: exerciseTypeLabel
      }).increment();
    }).catch(error => {
      Metrics.counter('exercise-mutation-call-error', {
        action: 'practical-exercise-resubmit-progress-delete-error',
        exerciseType: exerciseTypeLabel
      }).increment();
      reportErrorToLogFetch({
        loggingTitle: 'Practical Exercise Resubmitted Progress Deleted Error',
        action: 'practical-exercise-resubmitted-progress-deleted-error',
        error,
        additionalData: {
          contentId,
          lessonId,
          trackId
        }
      });
      FloatingAlertStore.addAlert({
        type: 'danger',
        message: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "learningCenter.practicalExercise.restartError",
          options: {
            contactUsLink: /*#__PURE__*/_jsx(ContactUsLink, {})
          }
        })
      });
    });
  };
  const requiredProduct = practicalExerciseRequirements === null || practicalExerciseRequirements === void 0 ? void 0 : practicalExerciseRequirements.requiredProducts[0];
  const requiredAccess = practicalExerciseRequirements === null || practicalExerciseRequirements === void 0 ? void 0 : practicalExerciseRequirements.requiredAccess[0];
  const error = practicalExerciseRequirementsError ? unwrapChirpError(practicalExerciseRequirementsError) : undefined;
  const loading = practicalExerciseRequirementsLoading;
  if (loading) {
    return /*#__PURE__*/_jsx(PracticalExerciseLoading, {});
  }
  if (error) {
    return /*#__PURE__*/_jsx(PracticalExerciseError, {
      error: error
    });
  }
  if (contentStatus === ContentProgressTypes.COMPLETED) {
    return /*#__PURE__*/_jsx(CompletedPracticalExerciseCondensed, {
      advanceToNextContent: onFinish,
      allowRevisitExercise: practicalExerciseType === 'AUTOMATICALLY_GRADED',
      contentId: Number(contentId),
      handleSetExerciseContext: handleTryExerciseAgain,
      lessonId: lessonId,
      trackId: trackId
    });
  }
  if (contentStatus === ContentProgressTypes.IN_REVIEW) {
    return /*#__PURE__*/_jsx(InReviewPracticalExerciseCondensed, {
      advanceToNextContent: onFinish,
      contentId: contentId,
      exerciseTypeLabel: exerciseTypeLabel,
      isManual: isManuallyGraded,
      isPassRequiredForCert: !!(practicalExercise !== null && practicalExercise !== void 0 && practicalExercise.graded),
      lessonId: lessonId,
      onResubmitClick: handleClickResubmit,
      resubmitType: "resubmit",
      title: (practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.name) || '',
      trackId: trackId
    });
  }
  if (contentStatus === ContentProgressTypes.STARTED && practicalExerciseType !== PracticalExerciseTypes.MANUALLY_GRADED_WITH_FILE_SUBMISSION) {
    return /*#__PURE__*/_jsx(InProgressPracticalExercise, {
      advanceToNextContent: onFinish,
      contentId: Number(contentId),
      exercise: practicalExercise,
      exerciseTypeLabel: exerciseTypeLabel,
      lessonId: lessonId,
      onResumeExercise: handleResumeExercise,
      trackId: trackId
    });
  }
  if (contentStatus === ContentProgressTypes.FAILED) {
    return /*#__PURE__*/_jsx(FailedPracticalExerciseCondensed, {
      advanceToNextContent: onFinish,
      contentId: Number(contentId),
      exerciseTypeLabel: exerciseTypeLabel,
      isPassRequiredForCert: !!(practicalExercise !== null && practicalExercise !== void 0 && practicalExercise.graded),
      lessonId: lessonId,
      onResubmitClick: handleClickResubmit,
      resubmitType: "resubmit",
      title: (practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.name) || '',
      trackId: trackId
    });
  }
  if (requiredProduct) {
    return /*#__PURE__*/_jsx(PracticalExerciseRequiredProduct, {
      advanceToNextContent: onFinish,
      onClickProductLink: handleClickThroughToProductPage,
      portalId: PortalIdParser.getPortalIdFromPath(),
      requiredProduct: requiredProduct
    });
  }
  if (requiredAccess) {
    return /*#__PURE__*/_jsx(PracticalExerciseRequiredAccess, {
      advanceToNextContent: onFinish,
      product: requiredAccess
    });
  }
  if (practicalExerciseType === PracticalExerciseTypes.MANUALLY_GRADED_WITH_FILE_SUBMISSION) {
    return /*#__PURE__*/_jsx(PracticalExerciseWithSubmission, {
      advanceToNextContent: onFinish,
      isPassRequiredForCert: !!(practicalExercise !== null && practicalExercise !== void 0 && practicalExercise.graded),
      isSubmitting: false,
      onSubmit: handleSubmitManualExercise,
      title: (practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.name) || ''
    });
  }
  return practicalExercise && /*#__PURE__*/_jsx(NotStartedPracticalExercise, {
    advanceToNextContent: onFinish,
    contentId: contentId,
    exercise: practicalExercise,
    exerciseTypeLabel: exerciseTypeLabel,
    isSimulationExercise: isSimulationExercise,
    lessonId: lessonId,
    onStartClick: handleStartExercise,
    trackId: trackId
  });
};
export const PracticalExerciseContainer = _ref => {
  let {
      lesson,
      onFinish
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  return lesson && onFinish ? /*#__PURE__*/_jsx(PracticalExercise, Object.assign({
    lesson: lesson,
    onFinish: onFinish
  }, props)) : null;
};
export default PracticalExerciseContainer;